<script setup>
import GenerateDialog from '@/components/common/GenerateDialog.vue'
import TableActionMenu from '@/components/common/TableActionMenu.vue'
import ActionBar from '@/components/common/ActionBar.vue'
import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { useStore } from 'vuex'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useHeightComposable } from '@/composables/useHeightComposable'

const props = defineProps({
  text: {
    type: String,
    default: ''
  },
  storeNamespace: {
    type: String,
    required: true
  },
  hoverPoint: {
    type: Object,
    default: null
  },
  generateButton: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  importButton: {
    type: Boolean,
    default: false
  },
  reduceHeight: {
    type: String,
    default: '0px'
  }
})

const store = useStore()

const lineTitle = ref(null)
const lineTitleHeight = ref(0)
const { mountObserver, unmountObserver } = useHeightComposable(lineTitleHeight, lineTitle)

const { x, y, showMenu, show, selectedItem } = actionMenuMixin()
const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()
const headers = [
  {
    title: 'X',
    value: 'geometry.coordinates.0',
    width: '25%',
    class: 'pl-0'
  },
  {
    title: 'Y',
    value: 'geometry.coordinates.1',
    width: '25%',
    class: 'pl-0'
  }
]
const coordinates = computed(() => store.state[props.storeNamespace]['coordinates'])
const generateLoading = computed(() => store.state[props.storeNamespace]['generateLoading'])
const dialog = computed(() => store.state[props.storeNamespace]['dialog'])
const loading = computed(() => store.state[props.storeNamespace]['loading'])
const sortCoordinatesX = () => store.dispatch(props.storeNamespace + '/sortCoordinatesX')
const update = () => store.dispatch(props.storeNamespace + '/update')
const emptyDirtyItems = () => store.dispatch(props.storeNamespace + '/emptyDirtyItems')
const toggleLoading = () => store.dispatch(props.storeNamespace + '/toggleLoading')

const setNestedParameter = (value, item, parameter) => {
  store.dispatch(props.storeNamespace + '/setNestedParameter', { value, item, parameter })
}

const add = (payload = null) => store.dispatch(props.storeNamespace + '/add', payload)

const toggleDialog = () => store.dispatch(props.storeNamespace + '/toggleDialog')
const deleteItem = (payload) => store.dispatch(props.storeNamespace + '/deleteItem', payload)

const enableDelete = computed(() => store.getters[props.storeNamespace + '/enableDelete'])
const saveChanges = async () => {
  try {
    await update()
    emptyDirtyItems()
    dirtyItems.value = []
  } catch (e) {
    toggleLoading()
  }
}
const addDirtyNewRow = (index) => {
  let item = coordinates.value[index]
  addDirty(item, 'coordinate.0')
  addDirty(item, 'coordinate.1')
}
const getEnableDelete = (item) => {
  if (item !== null && coordinates.value[item]) {
    const id = coordinates.value[item]['id']
    return enableDelete.value(id)
  }
  return true
}
const generate = async () => {
  emit('generate')
  dirtyItems.value = []
  toggleDialog()
}
const isSelected = (index) => {
  return (
    props.hoverPoint &&
    props.hoverPoint.storeNamespace === props.storeNamespace &&
    props.hoverPoint.pointIndex === index
  )
}
const getWrongTypo = (input) => {
  return input === '' || input === '-' ? 'border-bottom-red' : ''
}
const setParameter = (value, item, parameter) => {
  if (value !== '' && value !== null) {
    setNestedParameter(value, item, parameter)
  }
}
const emit = defineEmits(['generate', 'import', 'setActiveItem'])

onMounted(async () => {
  await mountObserver()
})

onUnmounted(() => {
  unmountObserver()
})
</script>

<template>
  <v-container ref="lineTitle">
    <span class="pl-2">
      {{ text }}
    </span>
    <ActionBar
      :disabled="readonly"
      border-bottom
      class="py-1 px-2"
      show-add
      show-save
      :show-import="importButton"
      :show-generate="generateButton"
      :loading-save="loading"
      :loading-generate="generateLoading"
      @add="(add(), addDirtyNewRow(0))"
      @save="saveChanges()"
      @generate="coordinates.length ? toggleDialog() : emit('generate')"
      @import="$emit('import')"
    >
      <template v-slot:left>
        <slot name="left"></slot>
      </template>
    </ActionBar>
  </v-container>
  <v-data-table
    class="overflow-y-auto"
    :height="`calc(100vh - ${reduceHeight} - ${lineTitleHeight}px)`"
    fixed-header
    disable-sort
    :items-per-page="-1"
    density="compact"
    :headers="headers"
    :items="coordinates ? coordinates : []"
    :loading="loading"
    color="primary"
    hover
  >
    <template #bottom> </template>
    <template v-slot:item="{ item, index }">
      <tr
        @click="emit('setActiveItem', index)"
        @contextmenu.prevent="readonly || show($event, index)"
        :class="{ 'selected-item': isSelected(index) }"
      >
        <td class="px-0">
          <v-text-field
            full-width
            :class="[
              getDirtyClass(item, 'coordinate.0'),
              getWrongTypo(item['coordinate'][0]),
              readonly ? 'disable-events' : '',
              'py-0 px-3 mt-0 text-field'
            ]"
            hide-details
            solo
            variant="plain"
            density="compact"
            type="number"
            @update:model-value="
              (setParameter($event, item, 'coordinate.0'), addDirty(item, 'coordinate.0'))
            "
            :model-value="item['coordinate'][0]"
            @change="sortCoordinatesX()"
          ></v-text-field>
        </td>
        <td class="px-0">
          <v-text-field
            full-width
            :class="[
              getDirtyClass(item, 'coordinate.1'),
              getWrongTypo(item['coordinate'][1]),
              readonly ? 'disable-events' : '',
              'py-0 px-3 mt-0 text-field'
            ]"
            hide-details
            solo
            variant="plain"
            density="compact"
            type="number"
            @update:model-value="
              (setParameter($event, item, 'coordinate.1'), addDirty(item, 'coordinate.1'))
            "
            :model-value="item['coordinate'][1]"
          ></v-text-field>
        </td>
      </tr>
    </template>
  </v-data-table>

  <GenerateDialog
    @close="toggleDialog"
    @generate="generate"
    :dialog="dialog"
    :loading="generateLoading"
    :text="text"
  />
  <TableActionMenu
    :selected-item="selectedItem"
    :show-menu="showMenu"
    @update:show-menu="showMenu = $event"
    :x="x"
    :y="y"
    :delete-button="getEnableDelete(selectedItem)"
    @action="
      deleteItem({
        index: selectedItem,
        id: coordinates[selectedItem]['id']
      })
    "
    @add="(add(selectedItem), addDirtyNewRow(selectedItem + 1))"
  />
</template>

<style scoped>
.v-container {
  padding: 0;
  margin: 0;
  min-width: 100%;
}
</style>
